<template>
  <b-form
    v-if="initialised"
    @submit.prevent="onSubmit"
    @keydown="form.errors.clear($event.target.id)"
  >
    <div class="row">
      <div class="col">
        <b-card>
          <template #header>
            <b>Designation</b>
          </template>

          <form-text v-model="form.name" name="name" maxlength="32" :required="true" />
          <form-designation-select
            v-model="form.parent"
            name="parent"
            :required="true"
            :exclude-ids="selfId"
            error-name="parent_id"
            @change="updateParent"
          />
          <div
            class="form-group row"
            :class="{ 'invalid-wrapper': form.errors.has('brand_mentor') }"
          >
            <div class="col-3 col-form-label text-right">Brand Mentor</div>
            <div class="col-8">
              <core-switch
                v-model="form.brand_mentor"
                variant="3d"
                color="primary"
                @change="form.errors.clear('brand_mentor')"
              />

              <div>
                <small
                  v-if="form.errors.has('brand_mentor')"
                  class="invalid-feedback"
                  v-text="form.errors.get('brand_mentor')"
                ></small>
              </div>
            </div>
          </div>
          <div
            class="form-group row"
            :class="{ 'invalid-wrapper': form.errors.has('delete_invoice') }"
          >
            <div class="col-3 col-form-label text-right">Can delete invoices</div>
            <div class="col-8">
              <core-switch
                v-model="form.delete_invoice"
                variant="3d"
                color="primary"
                @change="form.errors.clear('delete_invoice')"
              />

              <div>
                <small class="text-muted">
                  Users with admin roles can delete invoices regardless of this setting.
                </small>

                <small
                  v-if="form.errors.has('delete_invoice')"
                  class="invalid-feedback"
                  v-text="form.errors.get('delete_invoice')"
                ></small>
              </div>
            </div>
          </div>
          <div
            class="form-group row"
            :class="{ 'invalid-wrapper': form.errors.has('no_access_to_invoices') }"
          >
            <div class="col-3 col-form-label text-right">No access to Invoices</div>
            <div class="col-8">
              <core-switch
                v-model="form.no_access_to_invoices"
                variant="3d"
                color="primary"
                @change="form.errors.clear('no_access_to_invoices')"
              />

              <div>
                <small
                  v-if="form.errors.has('no_access_to_invoices')"
                  class="invalid-feedback"
                  v-text="form.errors.get('no_access_to_invoices')"
                ></small>
              </div>
            </div>
          </div>
          <div
            class="form-group row"
            :class="{ 'invalid-wrapper': form.errors.has('agency_access') }"
          >
            <div class="col-3 col-form-label text-right">Agency Access UI</div>
            <div class="col-8">
              <core-switch
                v-model="form.agency_access"
                variant="3d"
                color="primary"
                @change="form.errors.clear('agency_access')"
              />

              <div>
                <small
                  v-if="form.errors.has('agency_access')"
                  class="invalid-feedback"
                  v-text="form.errors.get('agency_access')"
                ></small>
              </div>
            </div>
          </div>
        </b-card>

        <b-card>
          <template #header>
            <b>Fields</b>
          </template>

          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>Default Value</th>
                <th>Type</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(field, index) in form.fields" :key="`field-${index}`">
                <td>
                  <input
                    v-model="field.name"
                    type="text"
                    class="form-control"
                    required
                    maxlength="32"
                    @keyup="form.errors.clear()"
                  />
                </td>
                <td>
                  <input
                    v-model="field.default"
                    type="text"
                    class="form-control"
                    maxlength="64"
                    @keyup="form.errors.clear()"
                  />
                </td>
                <td>
                  <select
                    v-model="field.type"
                    class="form-control custom-select capitalize"
                    @change="form.errors.clear()"
                  >
                    <option v-for="option in typeOptions" :key="`field-type-${option}`">
                      {{ option }}
                    </option>
                  </select>
                </td>
                <td>
                  <font-awesome-icon
                    :icon="['fal', 'times']"
                    class="cursor"
                    @click.stop="deleteItem(index)"
                  />
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3">
                  <button type="button" class="btn btn-primary btn-sm" @click.stop="addItem()">
                    <font-awesome-icon :icon="['fal', 'plus-circle']" />
                    Add Field
                  </button>
                </td>
              </tr>
            </tfoot>
          </table>
        </b-card>

        <b-card v-if="form.errors.any()">
          <div class="col-6 push-3 has-danger">
            <div class="form-control-feedback">
              Invalid input detected. Please correct the input.

              <div v-for="(error, index) in form.errors.errors" :key="index">
                {{ error[0] }}
              </div>
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <b-card-footer class="mb-5">
      <div class="row">
        <div class="col text-right">
          <button class="btn btn-primary" :disabled="form.notReady()">
            <font-awesome-icon v-if="form.busy" spin :icon="['fal', 'spinner']" />
            <font-awesome-icon v-else :icon="['fal', 'check']" />
            {{ crud.getAction() === "create" ? "Create" : "Save" }}
          </button>
        </div>
      </div>
    </b-card-footer>
  </b-form>
</template>

<script type="text/babel">
const typeOptions = ["Percentage", "Amount"];

export default {
  data() {
    return {
      typeOptions,
      crud: null,
      form: new window.AppForm({
        parent: null,
        parent_id: null,
        name: null,
        brand_mentor: false,
        delete_invoice: false,
        no_access_to_invoices: false,
        agency_access: false,
        fields: [],
      }),
      initialised: false,
    };
  },

  computed: {
    selfId() {
      const id = this.$lodash.get(this.crud, "data.id");

      return id ? [id] : [];
    },
  },

  created() {
    this.init();
  },

  methods: {
    init() {
      this.crud = new window.AppCrud("designation", ["parent", "fields"]);

      this.form.reset();

      if (this.crud.getAction() === "create") {
        this.initialised = true;
        return;
      }

      this.crud.initFormData(this.form).then(() => {
        this.form.fields = this.crud.data.fields;
        this.initialised = true;
      });
    },

    updateParent(parent) {
      this.form.parent_id = parent.id;
    },

    addItem() {
      this.form.fields.push({
        name: "",
        default: "",
        type: "percentage",
      });

      this.form.errors.clear();
    },

    deleteItem(index) {
      this.form.fields.splice(index, 1);

      this.form.errors.clear();
    },

    onSubmit() {
      this.crud.submit(this.form).then((response) => {
        if (this.crud.getAction() === "create") {
          window.toastr.success("New Designation created!", "Success");

          this.$router.push(
            {
              name: "designations.edit",
              params: { id: response.voucher.id },
            },
            () => {
              this.init();
            }
          );
        } else {
          this.init();
          window.toastr.success("Designation updated!", "Success");
        }
      });
    },
  },
};
</script>
